import {
  Box,
  Button,
  Stack,
  Tab,
  Tabs,
  Card,
  CardContent,
  Chip,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Tooltip,
} from "@mui/material";

import { SyntheticEvent, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { Add, Search } from "@mui/icons-material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { PrimaryButton, SecondaryButton } from "../../components/common";
import { ListaDeIntervenções } from "../medicalcare/components/ListaDeIntervenções";
import { Tratamentos } from "./components/Tratamentos";

import { useNavigate } from "react-router-dom";
import { useContractContext } from "../../contexts/ContractContext";
import { useOrganizationContext } from "../../contexts/OrganizationContext";
import { needsSpecialReceituarioPdf } from "src-next/modules/sncr";
import { Certificate } from "../medicalcare/components/Certificate/Certificate";
import { ExamsFields } from "../medicalcare/components/ExamsFields";
import { ProcedureAndCertificate } from "../medicalcare/components/ProcedureAndCertificate";
import { CreateProtocolDialog } from "../medicalcare/components/CreateProtocolDialog";
import { SelectProtocolDialog } from "../medicalcare/components/SelectProtocolDialog";
import { useQuestionnaireManagement } from "src-next/modules/questionnaire-builder/questionnaire-management/hooks";
import { QUESTIONNAIRE_UNKNOWN_STATUS_TOOLTIP } from "src-next/modules/questionnaire-builder/questionnaire-management/utils/questionnaireManagementConstants";
import type { QuestionnairePayload } from "src-next/modules/questionnaire-builder/form-builder/types";
import { isQuestionnaireSelectableForClinicalUse } from "@/utils/questionnaireClinicalSelection";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  teste?: any;
}

function a11yProps(index: number) {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
}

export function CreateProtocolContent({
  setSelected,
  defaultItens,
  setId,
  id,
}: any) {
  const { currentContract } = useContractContext();
  const { organization } = useOrganizationContext();
  const [selectProtocol, setSelectProtocol] = useState<boolean>(false);
  const [createDialog, setCreateDialog] = useState<boolean>(false);

  const [appliedProtocol, setAppliedProtocol] = useState<Array<any>>([]);

  const [expandedList, setExpandedList] = useState<boolean>(true);

  const [tabValue, setTabValue] = useState(0);
  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const [tabValueMedication, setTabValueMedication] = useState(0);
  const handleChangeMedication = (event: SyntheticEvent, newValue: number) => {
    setTabValueMedication(newValue);
  };

  const methods = useForm();
  const { handleSubmit, control, reset, setValue } = methods;

  const [interventionsList, setInterventionsList] = useState<Array<any>>([]);
  const [selectedItem, setSelectedItem] = useState<number>();

  const [clean, setClean] = useState<boolean>(false);

  /**
   * Item de questionário selecionado dentro do protocolo.
   *
   * Decisão:
   * - Modelamos questionários como itens dentro de `interventionsList`.
   * - Isso reaproveita o fluxo existente: `CreateProtocolDialog` recebe `itens`
   *   e `SelectProtocolDialog` aplica protocolos via `addItem(...)`.
   *
   * Campos mínimos:
   * - `questionnaireId`: ID lógico do FHIR Questionnaire
   * - `title`: título para exibição
   */
  type ProtocolQuestionnaireItem = {
    type: "questionnaire";
    questionnaireId: string;
    title: string;
    /**
     * Campos adicionais para compatibilidade com fluxos existentes
     * (ex.: seleção no `CreateProtocolDialog` usa `${type}-${name}-${label}`).
     */
    name: string;
    label: string;
    usageType?: QuestionnairePayload["usageType"];
    protocol?: { display: string; reference: string };
  };

  const {
    questionnaires: availableQuestionnaires,
    isLoading: isQuestionnairesLoading,
    errorMessage: questionnairesErrorMessage,
  } = useQuestionnaireManagement({
    organizationId: organization?.id,
  });

  /** Protocolo: fase intervenção — apenas pós-consulta e só active/unknown (não rascunho). */
  const filteredQuestionnairesForSelection = availableQuestionnaires.filter(
    (questionnaire) =>
      questionnaire.usageType === "postConsultation" &&
      isQuestionnaireSelectableForClinicalUse(questionnaire),
  );

  const functions = {
    handleAdd: (item: any) => {
      setInterventionsList((prev: any) => [
        ...prev?.filter((e: any) => e?.name),
        item,
      ]);
      setClean(true);
    },
    handleEdit: (item: any) => {
      setInterventionsList((prevState: any) => {
        const updatedItems = [...prevState];
        const index = updatedItems.findIndex((el, i) => i === selectedItem);
        if (index !== -1) {
          updatedItems[index] = item;
        }

        return updatedItems;
      });
      setSelectedItem(undefined);
      setClean(true);
    },
    handleSign: (index: any, signedBase64: any) => {
      setInterventionsList((prevState: any) => {
        const updatedItems = [...prevState];

        updatedItems[index] = {
          ...updatedItems[index],
          signedBase64,
          sign: true,
        };

        return updatedItems;
      });
      setSelectedItem(undefined);
      setClean(true);
    },
    handleRemove: (index: number) => {
      setSelectedItem(undefined);

      setInterventionsList((prev: any) =>
        prev.filter((item: any, i: number) => i !== index),
      );
    },
    handleSelect: (item: any, index: any) => {
      setSelectedItem(index);

      // Tratamentos: medication, supplement, herbalMedicine
      if (
        item?.type === "medication" ||
        item?.type === "supplement" ||
        item?.type === "herbalMedicine"
      ) {
        setTabValue(0);
        // Define a aba correta baseada no tipo
        if (item?.type === "medication") {
          setTabValueMedication(0);
        } else if (item?.type === "supplement") {
          setTabValueMedication(1);
        } else if (item?.type === "herbalMedicine") {
          setTabValueMedication(2);
        }
        // Reseta apenas os campos relevantes para medicamentos
        reset({
          medication: item?.inputValue,
          quantity: item?.quantity?.value || item?.quantity,
          frequency: item.frequency,
          note: item.note,
          time: item.time,
          form: item.formValue,
        });
      }
      if (item.type === "procedure") {
        setTabValue(2);
        reset({
          procedure: item.inputValue,
          note: item.note,
        });
      }
      if (item.type === "service") {
        setTabValue(1);
        reset({
          service: item.inputValue,
          condition: item.condition,
          category: item.category || {},
          note: item.note || "",
        });
      }
      if (item.type === "certificate") {
        setTabValue(3);
        reset({
          certificate: item.note,
        });
      }
    },
    handleRemoveProtocol: (index: number, value: string) => {
      setAppliedProtocol((prev: any) =>
        prev.filter((item: any, i: number) => i !== index),
      );
      setInterventionsList((prev: any) =>
        prev.filter(
          (item: any, i: number) => item?.protocol?.reference !== value,
        ),
      );
    },
  };

  function isQuestionnaireSelected(questionnaireId: string): boolean {
    return interventionsList.some(
      (item: any) =>
        item?.type === "questionnaire" &&
        item?.questionnaireId === questionnaireId,
    );
  }

  function toggleQuestionnaireSelection(questionnaire: QuestionnairePayload) {
    const questionnaireId = questionnaire.id;
    if (!questionnaireId) return;
    if (!isQuestionnaireSelectableForClinicalUse(questionnaire)) return;

    const existingIndex = interventionsList.findIndex(
      (item: any) =>
        item?.type === "questionnaire" &&
        item?.questionnaireId === questionnaireId,
    );

    if (existingIndex !== -1) {
      functions.handleRemove(existingIndex);
      return;
    }

    const item: ProtocolQuestionnaireItem = {
      type: "questionnaire",
      questionnaireId,
      title: questionnaire.title,
      name: questionnaire.title,
      // Mantemos o `label` estável e único para evitar colisões na chave usada no dialog.
      label: questionnaireId,
      usageType: questionnaire.usageType,
    };

    functions.handleAdd(item);
  }

  const selectedInterventions = {
    all: interventionsList,
    medicationSpecial: interventionsList?.filter((el: any) =>
      needsSpecialReceituarioPdf(el),
    ),
    medicationSimple: interventionsList?.filter(
      (el: any) =>
        (el?.type === "medication" ||
          el?.type === "supplement" ||
          el?.type === "herbalMedicine") &&
        !needsSpecialReceituarioPdf(el),
    ),
    service: interventionsList?.filter((el: any) => el?.type === "service"),
    procedure: interventionsList?.filter((el: any) => el?.type === "procedure"),
    certificates: interventionsList?.filter(
      (el: any) => el?.type === "certificate",
    ),
    questionnaires: interventionsList?.filter(
      (el: any) => el?.type === "questionnaire",
    ),
  };

  const action =
    selectedItem === undefined ? functions.handleAdd : functions.handleEdit;
  const isEdit = selectedItem !== undefined;

  function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        style={{ width: "100%" }}
        hidden={value !== index}
        id={`vertical-tabpanel-${index}`}
        aria-labelledby={`vertical-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Stack
            sx={{ pl: 3, width: "100%" }}
            direction="row"
            justifyContent="space-between"
          >
            {" "}
            <Stack
              width="100%"
              direction="row"
              justifyContent="space-between"
              spacing={2}
              pr={3}
            >
              {children}

              <Stack
                width="100%"
                minWidth="600px"
                overflow="auto"
                maxHeight="60vh"
                paddingRight={3}
              >
                <ListaDeIntervenções
                  selectedInterventions={selectedInterventions}
                  functions={functions}
                  setCreateDialog={setCreateDialog}
                  expandedList={expandedList}
                  setExpandedList={setExpandedList}
                  appliedProtocol={appliedProtocol}
                />
              </Stack>
            </Stack>
          </Stack>
        )}
      </div>
    );
  }

  const navigate = useNavigate();

  useEffect(() => {
    setInterventionsList([]);
    if (defaultItens?.itens?.length && !interventionsList?.length) {
      defaultItens?.itens?.map((el: any) => functions?.handleAdd(el));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultItens]);

  useEffect(() => {
    if (clean) {
      reset();
      // Reset específico da tab de exames
      reset({
        service: {},
        condition: [],
        category: {},
        note: "",
      });
      setClean(false);
    }
  }, [clean, reset]);

  return (
    <Stack spacing={2}>
      <Stack pl={2}>
        <Button
          sx={{ width: "fit-content", textTransform: "none" }}
          startIcon={<Search />}
          onClick={() => setSelectProtocol(true)}
        >
          Selecionar protocolo
        </Button>
      </Stack>
      <Box
        sx={{
          flexGrow: 1,
          bgcolor: "background.paper",
          display: "flex",
          height: "fit-content",
        }}
      >
        <Tabs
          orientation="vertical"
          variant="scrollable"
          value={tabValue}
          onChange={handleChange}
          aria-label="Vertical tabs example"
          sx={{ borderRight: 1, borderColor: "divider" }}
        >
          <Tab label="Tratamentos" {...a11yProps(0)} />
          <Tab label="Exames" {...a11yProps(1)} />
          <Tab label="Procedimentos" {...a11yProps(2)} />
          <Tab label="Relatórios" {...a11yProps(3)} />
          <Tab label="Questionários" {...a11yProps(4)} />
          {/* <Tab label="Referências" {...a11yProps(5)} />
          <Tab label="Descrição" {...a11yProps(6)} /> */}
        </Tabs>

        <TabPanel value={tabValue} index={0} teste="teste">
          <Tratamentos
            tabValueMedication={tabValueMedication}
            handleChangeMedication={handleChangeMedication}
            clean={clean}
            control={control}
            setValue={setValue}
            setClean={setClean}
            handleSubmit={handleSubmit}
            action={action}
            isEdit={isEdit}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Stack width="100%" maxHeight="60vh" overflow="auto">
            <ExamsFields
              control={control}
              setValue={setValue}
              action={action}
              setClean={setClean}
              handleSubmit={handleSubmit}
              isEdit={isEdit}
            />
          </Stack>
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <Stack width="100%" maxHeight="60vh" overflow="auto">
            <ProcedureAndCertificate
              control={control}
              setValue={setValue}
              action={action}
              setClean={setClean}
              handleSubmit={handleSubmit}
              isEdit={isEdit}
            />
          </Stack>
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          <Stack width="100%" maxHeight="60vh" overflow="auto">
            <Certificate
              control={control}
              setValue={setValue}
              action={action}
              setClean={setClean}
              handleSubmit={handleSubmit}
              isEdit={isEdit}
            />
          </Stack>
        </TabPanel>
        <TabPanel value={tabValue} index={4}>
          <Stack width="100%" maxHeight="60vh" overflow="auto" spacing={2}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems="stretch"
            >
              <Stack sx={{ flex: 1, minWidth: 0 }} spacing={1.5}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Questionários de pós-consulta
                </Typography>

                <List
                  dense
                  disablePadding
                  sx={{
                    borderRadius: 1,

                    borderColor: "divider",
                    maxHeight: 260,
                    overflow: "auto",
                  }}
                >
                  {isQuestionnairesLoading ? (
                    <ListItem>
                      <ListItemText
                        primary="Carregando questionários..."
                        secondary="Aguarde alguns segundos."
                      />
                    </ListItem>
                  ) : questionnairesErrorMessage ? (
                    <ListItem>
                      <ListItemText
                        primary="Não foi possível carregar os questionários."
                        secondary={questionnairesErrorMessage}
                      />
                    </ListItem>
                  ) : filteredQuestionnairesForSelection.length === 0 ? (
                    <ListItem>
                      <ListItemText
                        primary="Nenhum questionário encontrado."
                        secondary="Crie um questionário ou ajuste os filtros."
                      />
                    </ListItem>
                  ) : (
                    filteredQuestionnairesForSelection.map((questionnaire) => {
                      const questionnaireId = questionnaire.id;
                      const isSelected = questionnaireId
                        ? isQuestionnaireSelected(questionnaireId)
                        : false;

                      return (
                        <ListItem
                          key={questionnaireId || questionnaire.title}
                          secondaryAction={
                            <Checkbox
                              edge="end"
                              checked={isSelected}
                              onChange={() => {
                                toggleQuestionnaireSelection(questionnaire);
                              }}
                            />
                          }
                        >
                          <ListItemButton
                            onClick={() => {
                              toggleQuestionnaireSelection(questionnaire);
                            }}
                          >
                            <ListItemText
                              primary={
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                  component="span"
                                >
                                  <Typography
                                    variant="body2"
                                    fontWeight={500}
                                    component="span"
                                  >
                                    {questionnaire.title}
                                  </Typography>
                                  {questionnaire.status === "unknown" ? (
                                    <Tooltip
                                      title={
                                        QUESTIONNAIRE_UNKNOWN_STATUS_TOOLTIP
                                      }
                                      arrow
                                    >
                                      <ErrorOutlineIcon
                                        sx={{
                                          fontSize: 22,
                                          color: "error.main",
                                          display: "block",
                                        }}
                                        aria-label="Informação sobre versão vista pelos pacientes"
                                      />
                                    </Tooltip>
                                  ) : null}
                                </Stack>
                              }
                              secondary={
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Pós-consulta
                                </Typography>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })
                  )}
                </List>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={() => navigate("/questionnaireBuilder")}
                  >
                    Novo questionário
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </TabPanel>
      </Box>
      {/* <Stack justifySelf="start" marginTop={3}>
        <SecondaryButton
          onClick={() => {
            setSelected("");
            setId("");
          }}
          width="180px"
          height="50px"
        >
          Voltar
        </SecondaryButton>
      </Stack> */}
      <Stack width="100%" direction="row" justifyContent="space-between">
        <SecondaryButton
          onClick={() => navigate(-1)}
          width="180px"
          height="50px"
        >
          Voltar
        </SecondaryButton>

        <PrimaryButton
          width="180px"
          height="50px"
          onClick={() => setCreateDialog(true)}
          disabled={!selectedInterventions?.all?.length}
        >
          Confirmar
        </PrimaryButton>
      </Stack>

      {selectProtocol && (
        <SelectProtocolDialog
          isOpen={selectProtocol}
          handleClose={() => setSelectProtocol(false)}
          addItem={functions?.handleAdd}
          setAppliedProtocol={setAppliedProtocol}
        />
      )}
      {createDialog && (
        <CreateProtocolDialog
          id={id}
          itens={selectedInterventions?.all}
          isOpen={createDialog}
          handleClose={() => setCreateDialog(false)}
        />
      )}
    </Stack>
  );
}
